"use client";
import * as React from "react";
import EmojiSuggestionList, { type EmojiSuggestionListRef } from "@/components/ui/emoji-suggestion-list";
import { getLoadedEmojiIndex, loadEmojiIndex, searchEmoji, type EmojiMatch } from "@/lib/emoji-index";
import { OWNS_ESCAPE_ATTRIBUTE } from "@/lib/escape-ownership";
import {
  completedShortcodeAt,
  emojiQueryAt,
  symbolShortcutAt,
  type TextReplacement
} from "@/lib/plain-text-shortcuts";
import { isImeComposing } from "@/lib/ime-composition";

type TextField = HTMLInputElement | HTMLTextAreaElement;

export interface TextShortcutHandlers<E extends TextField> {
  onChange?: React.ChangeEventHandler<E>;
  onKeyDown?: React.KeyboardEventHandler<E>;
  onSelect?: React.ReactEventHandler<E>;
  onBlur?: React.FocusEventHandler<E>;
}

export interface UseTextShortcutsOptions<E extends TextField> extends TextShortcutHandlers<E> {
  enabled: boolean;
}

export interface UseTextShortcutsResult<E extends TextField> {
  /** Spread onto the field in place of the caller's own handlers, which these call. */
  fieldProps: Required<TextShortcutHandlers<E>> & { [OWNS_ESCAPE_ATTRIBUTE]?: "" };
  /** The `:` emoji menu; render it anywhere next to the field. */
  menu: React.ReactNode;
}

interface MenuState {
  from: number;
  query: string;
  items: EmojiMatch[];
}

/** After a failed load the menu stays closed this long, as in `emojiSuggestion.ts`. */
const LOAD_RETRY_MS = 30_000;
let lastLoadFailureAt = 0;
const loadRecentlyFailed = () => Date.now() - lastLoadFailureAt < LOAD_RETRY_MS;

/** Whether replacing `[from, to)` with `text` keeps the field within its `maxLength`. */
function fitsMaxLength(field: TextField, from: number, to: number, text: string): boolean {
  return field.maxLength < 0 || field.value.length - (to - from) + text.length <= field.maxLength;
}

function setNativeValue(field: TextField, value: string): void {
  const proto = field instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  // The prototype setter bypasses React's value tracker, so the input event below reaches onChange.
  Object.getOwnPropertyDescriptor(proto, "value")?.set?.call(field, value);
}

/**
 * The rich editors' symbol shortcuts (`->` to `→`, BRE-7787) and `:` emoji menu
 * (BRE-8060) for a plain `<input>` or `<textarea>` (BRE-8381).
 *
 * Every edit goes through a real input event, so the caller's `onChange` sees
 * the converted text exactly as if it had been typed. Only `insertText` is
 * converted: paste, IME composition and programmatic writes stay as they are.
 */
export function useTextShortcuts<E extends TextField>({
  enabled,
  ...handlers
}: UseTextShortcutsOptions<E>): UseTextShortcutsResult<E> {
  const latest = React.useRef(handlers);
  latest.current = handlers;
  const enabledRef = React.useRef(enabled);
  enabledRef.current = enabled;

  const fieldRef = React.useRef<E | null>(null);
  const listRef = React.useRef<EmojiSuggestionListRef>(null);
  const [menu, setMenuState] = React.useState<MenuState | null>(null);
  const menuRef = React.useRef<MenuState | null>(null);
  // Escape closes the menu for the rest of that `:` token.
  const dismissed = React.useRef<{ from: number; query: string } | null>(null);
  const lastReplacement = React.useRef<(TextReplacement & { caret: number }) | null>(null);
  // Set while this hook's own edit dispatches its input event.
  const applying = React.useRef(false);
  // Whether that event reached onChange; a browser that fires it late would otherwise lose the edit.
  const delivered = React.useRef(false);
  const awaitingIndex = React.useRef(false);

  const setMenu = React.useCallback((next: MenuState | null) => {
    if (menuRef.current === next) return;
    menuRef.current = next;
    setMenuState(next);
  }, []);

  const refreshMenu = React.useCallback(
    (field: E) => {
      const caret = field.selectionStart;
      if (!enabledRef.current || caret === null || caret !== field.selectionEnd || field.ownerDocument.activeElement !== field) {
        setMenu(null);
        return;
      }
      const token = emojiQueryAt(field.value.slice(0, caret));
      if (!token) {
        dismissed.current = null;
        setMenu(null);
        return;
      }
      const gone = dismissed.current;
      if (gone) {
        if (gone.from === token.from && token.query.startsWith(gone.query)) {
          setMenu(null);
          return;
        }
        dismissed.current = null;
      }
      const index = getLoadedEmojiIndex();
      if (!index) {
        requestIndex(field);
        setMenu(loadRecentlyFailed() ? null : { ...token, items: [] });
        return;
      }
      const current = menuRef.current;
      if (current && current.items.length > 0 && current.from === token.from && current.query === token.query) return;
      const items = searchEmoji(index, token.query);
      setMenu(items.length > 0 ? { ...token, items } : null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- requestIndex is hoisted and only reads refs
    [setMenu]
  );

  function requestIndex(field: E): void {
    if (awaitingIndex.current || getLoadedEmojiIndex() || loadRecentlyFailed()) return;
    awaitingIndex.current = true;
    loadEmojiIndex()
      .catch((error: unknown) => {
        lastLoadFailureAt = Date.now();
        // Not the shared logger: it reads process.env at load, and ui/* ships in the design-sync bundle.
        console.warn("Emoji data failed to load", error);
      })
      .then(() => {
        awaitingIndex.current = false;
        if (fieldRef.current !== field) return;
        convertPendingShortcode(field);
        refreshMenu(field);
      });
  }

  /** A `:shortcode:` finished before the emoji data loaded, still just before the caret. */
  function convertPendingShortcode(field: E): void {
    const index = getLoadedEmojiIndex();
    const caret = field.selectionStart;
    if (!index || !enabledRef.current || field.ownerDocument.activeElement !== field) return;
    if (caret === null || caret !== field.selectionEnd) return;
    const pending = completedShortcodeAt(field.value.slice(0, caret), index);
    if (!pending || !fitsMaxLength(field, pending.from, pending.to, pending.replacement)) return;
    replaceRange(field, pending.from, pending.to, pending.replacement);
    lastReplacement.current = { ...pending, caret: pending.from + pending.replacement.length };
  }

  /** Replaces `[from, to)` with `text` through an input event, leaving the caret after it. */
  const replaceRange = React.useCallback((field: E, from: number, to: number, text: string) => {
    const expected = field.value.slice(0, from) + text + field.value.slice(to);
    applying.current = true;
    delivered.current = false;
    try {
      field.setSelectionRange(from, to);
      const doc = field.ownerDocument;
      // execCommand keeps the edit on the browser's native undo stack.
      const viaCommand =
        doc.activeElement === field && typeof doc.execCommand === "function" && doc.execCommand("insertText", false, text);
      if (!viaCommand || field.value !== expected || !delivered.current) {
        if (field.value !== expected) setNativeValue(field, expected);
        field.dispatchEvent(new Event("input", { bubbles: true }));
      }
    } finally {
      applying.current = false;
    }
    field.setSelectionRange(from + text.length, from + text.length);
  }, []);

  const pick = React.useCallback(
    (item: EmojiMatch) => {
      const field = fieldRef.current;
      const open = menuRef.current;
      if (!field || !open) return;
      const to = field.selectionStart ?? open.from + 1 + open.query.length;
      if (!fitsMaxLength(field, open.from, to, item.emoji)) {
        setMenu(null);
        return;
      }
      replaceRange(field, open.from, to, item.emoji);
      refreshMenu(field);
    },
    [refreshMenu, replaceRange, setMenu]
  );

  React.useEffect(() => {
    if (!enabled) setMenu(null);
  }, [enabled, setMenu]);

  const onChange = React.useCallback(
    (event: React.ChangeEvent<E>) => {
      const field = event.currentTarget;
      fieldRef.current = field;
      if (!enabledRef.current || applying.current) {
        if (applying.current) delivered.current = true;
        latest.current.onChange?.(event);
        return;
      }
      lastReplacement.current = null;
      const native = event.nativeEvent as Partial<InputEvent>;
      const caret = field.selectionStart;
      if (native.inputType === "insertText" && !native.isComposing && caret !== null && caret === field.selectionEnd) {
        const before = field.value.slice(0, caret);
        const index = getLoadedEmojiIndex();
        const replacement = symbolShortcutAt(before) ?? (index ? completedShortcodeAt(before, index) : null);
        // A shortcode can be shorter than its emoji (`:england:` is 9 units, its flag 14).
        if (replacement && fitsMaxLength(field, replacement.from, replacement.to, replacement.replacement)) {
          // Its own input event has already given the caller the converted text.
          replaceRange(field, replacement.from, replacement.to, replacement.replacement);
          lastReplacement.current = { ...replacement, caret: replacement.from + replacement.replacement.length };
          refreshMenu(field);
          return;
        }
        if (!index && native.data === ":") requestIndex(field);
      }
      latest.current.onChange?.(event);
      refreshMenu(field);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- requestIndex is hoisted and only reads refs
    [refreshMenu, replaceRange]
  );

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent<E>) => {
      const field = event.currentTarget;
      fieldRef.current = field;
      const composing = isImeComposing(event);
      const consume = () => {
        event.preventDefault();
        event.stopPropagation();
      };
      const open = menuRef.current;
      // An IME's committing Enter while the menu is open: the browser commits the text, and the
      // field's own Enter-to-save/send never sees it (not every caller guards on composition).
      if (enabledRef.current && open && composing && event.key === "Enter") {
        event.stopPropagation();
        return;
      }
      if (enabledRef.current && open && !composing) {
        if (event.key === "Escape") {
          dismissed.current = { from: open.from, query: open.query };
          setMenu(null);
          consume();
          return;
        }
        // Enter and Tab never reach a submit or save while the menu is open, even before it mounts.
        const handled = listRef.current
          ? listRef.current.onKeyDown({ event: event.nativeEvent })
          : event.key === "Enter" || event.key === "Tab";
        if (handled) {
          consume();
          return;
        }
      }
      const undo = lastReplacement.current;
      if (enabledRef.current && event.key === "Backspace" && !composing && undo) {
        lastReplacement.current = null;
        if (
          field.selectionStart === undo.caret &&
          field.selectionEnd === undo.caret &&
          field.value.slice(undo.from, undo.caret) === undo.replacement &&
          // `↔` restores three characters where the field held two before it.
          fitsMaxLength(field, undo.from, undo.caret, undo.typed)
        ) {
          event.preventDefault();
          replaceRange(field, undo.from, undo.caret, undo.typed);
          refreshMenu(field);
          return;
        }
      }
      latest.current.onKeyDown?.(event);
    },
    [refreshMenu, replaceRange, setMenu]
  );

  const onSelect = React.useCallback(
    (event: React.SyntheticEvent<E>) => {
      const field = event.currentTarget;
      fieldRef.current = field;
      const undo = lastReplacement.current;
      // Backspace restores only right after the conversion, not after the caret went away and back.
      if (undo && (field.selectionStart !== undo.caret || field.selectionEnd !== undo.caret)) {
        lastReplacement.current = null;
      }
      latest.current.onSelect?.(event);
      if (enabledRef.current) refreshMenu(event.currentTarget);
    },
    [refreshMenu]
  );

  const onBlur = React.useCallback(
    (event: React.FocusEvent<E>) => {
      dismissed.current = null;
      lastReplacement.current = null;
      setMenu(null);
      latest.current.onBlur?.(event);
    },
    [setMenu]
  );

  const menuNode =
    enabled && menu ? (
      <EmojiSuggestionList
        ref={listRef}
        items={menu.items}
        command={pick}
        anchorRef={fieldRef}
        onDismiss={() => setMenu(null)}
        testId="text-shortcuts-emoji-menu"
      />
    ) : null;

  return {
    fieldProps: {
      onChange,
      onKeyDown,
      onSelect,
      onBlur,
      // Keeps an enclosing dialog or sheet from closing on the menu's Escape.
      ...(enabled && menu ? { [OWNS_ESCAPE_ATTRIBUTE]: "" as const } : {})
    },
    menu: menuNode
  };
}
